import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { validateKYCForFinancialOperations, KYCValidationResult } from '../../lib/kycValidation';
import { isMasterAdminViewSession } from '../../lib/masterAdminAccess';
import { withdrawalEscrowService } from '../../lib/services/withdrawalEscrowService';
import { getSessionHeaders } from '../../lib/serverSession';
import { ProofOfPaymentViewer } from './ProofOfPaymentViewer';
import { LockedWalletManager, NETWORK_TO_LOCKED_KEY } from './LockedWalletManager';
import type { LockedWallet } from './LockedWalletManager';
// emailProviderHelper is now used by the shared WithdrawalEmailVerificationModal
import { WithdrawalEmailVerificationModal } from '../withdrawals/WithdrawalEmailVerificationModal';
import { normalizeWithdrawalNetwork } from '../../lib/networkNormalization';
import { floorToCents, formatFlooredUsd } from '../../lib/withdrawalMoney';
import { DIVIDENDS_USER_VISIBLE } from '../../lib/dividendsUserVisible';

interface CommissionWithdrawalProps {
  userId: number;
  availableUSDT: number;
  displayedBalanceUSDT?: number;
  computedWithdrawableUSDT?: number;
  withdrawalRequestMaxUSDT?: number;
  networkFeeUSDT?: number;
  withdrawalBlocked?: boolean;
  withdrawalBlockReasonCode?: string | null;
  withdrawalBlockReasonMessage?: string | null;
  availableShares: number;
  currentSharePrice: number;
  onWithdrawalRequest: () => void;
  userEmail?: string;
  onNavigateToSupport?: () => void;
}

// Helper to mask an email address for privacy (e.g. "j***@gmail.com")
const __maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return '***@***.***';
  const [local, domain] = email.split('@');
  if (local.length <= 1) return `${local}***@${domain}`;
  return `${local[0]}***@${domain}`;
};

export const CommissionWithdrawal: React.FC<CommissionWithdrawalProps> = ({
  userId,
  availableUSDT,
  displayedBalanceUSDT,
  computedWithdrawableUSDT,
  withdrawalRequestMaxUSDT,
  networkFeeUSDT,
  withdrawalBlocked = false,
  withdrawalBlockReasonCode = null,
  withdrawalBlockReasonMessage = null,
  availableShares,
  currentSharePrice,
  onWithdrawalRequest,
  userEmail,
  onNavigateToSupport
}) => {
  const { t } = useTranslation('common');
  const [kycValidation, setKycValidation] = useState<KYCValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawalType, setWithdrawalType] = useState<'usdt' | 'shares'>('usdt');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [network] = useState('USDT-BEP20'); // BSC only — no network selection
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userWithdrawals, setUserWithdrawals] = useState<any[]>([]);
  const [confirmingWithdrawal, setConfirmingWithdrawal] = useState<string | null>(null);
  const [proofViewerOpen, setProofViewerOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState<any>(null);

  // System-wide withdrawal toggle state
  const [withdrawalsEnabled, setWithdrawalsEnabled] = useState(true);

  // Locked wallet state
  const [lockedWallets, setLockedWallets] = useState<LockedWallet[]>([]);
  const [walletLocked, setWalletLocked] = useState(false);

  // When locked wallets load, auto-fill the wallet address for the selected network
  const handleLockedWalletsLoaded = (wallets: LockedWallet[]) => {
    setLockedWallets(wallets);
    updateWalletFromLocked(wallets, network);
  };

  // Auto-fill wallet when network changes
  // v4.7.18: Withdrawals accept BOTH centralized and decentralized wallets.
  // Prefer centralized when both exist, otherwise fall back to decentralized / legacy untyped.
  const updateWalletFromLocked = (wallets: LockedWallet[], selectedNetwork: string) => {
    const lockedKey = NETWORK_TO_LOCKED_KEY[selectedNetwork];
    if (!lockedKey) { setWalletLocked(false); return; }
    const networkWallets = wallets.filter(w => normalizeWithdrawalNetwork(w.network) === normalizeWithdrawalNetwork(lockedKey) && w.is_active);
    const match =
      networkWallets.find(w => w.wallet_type === 'centralized') ||
      networkWallets.find(w => w.wallet_type === 'decentralized') ||
      networkWallets.find(w => !w.wallet_type);
    if (match) {
      setWalletAddress(match.wallet_address);
      setWalletLocked(true);
    } else {
      if (wallets.length > 0) {
        setWalletAddress('');
      }
      setWalletLocked(false);
    }
  };

  // Re-check locked wallet when network selection changes
  useEffect(() => {
    if (lockedWallets.length > 0) {
      updateWalletFromLocked(lockedWallets, network);
    }
  }, [network]);

  // Pending verification withdrawals state (magic link only - no PIN)
  const [pendingVerificationWithdrawals, setPendingVerificationWithdrawals] = useState<any[]>([]);
  const [resendingVerification, setResendingVerification] = useState<string | null>(null);
  const [cancellingPendingId, setCancellingPendingId] = useState<string | null>(null);

  // Email confirmation screen state (shown after successful USDT withdrawal submission requiring verification)
  const [emailConfirmationData, setEmailConfirmationData] = useState<{
    withdrawalId: string;
    amount: number;
    walletAddress: string;
    network: string;
    linkSent: boolean;
    expiresAt?: string | null;
  } | null>(null);

  // NOTE: All verification modal state (polling, countdown, BroadcastChannel, Escape blocking,
  // auto-close) is now managed internally by the shared WithdrawalEmailVerificationModal component.

  const computedWithdrawable = typeof computedWithdrawableUSDT === 'number'
    ? computedWithdrawableUSDT
    : (typeof displayedBalanceUSDT === 'number' ? displayedBalanceUSDT : availableUSDT);
  const withdrawalNetworkFee = typeof networkFeeUSDT === 'number' ? networkFeeUSDT : 2;
  const maxWithdrawalAmount = floorToCents(Math.max(
    0,
    typeof withdrawalRequestMaxUSDT === 'number'
      ? withdrawalRequestMaxUSDT
      : computedWithdrawable - withdrawalNetworkFee
  ));
	  const enteredWithdrawalAmount = parseFloat(withdrawalAmount);
	  const validEnteredWithdrawalAmount = Number.isFinite(enteredWithdrawalAmount) ? enteredWithdrawalAmount : 0;
	  const withdrawalAmountExceedsMax = withdrawalType === 'usdt' && validEnteredWithdrawalAmount > maxWithdrawalAmount;
	  const withdrawalSubmitDisabled = isSubmitting
	    || !kycValidation?.canWithdraw
	    || (withdrawalType === 'usdt' && !walletLocked)
	    || (withdrawalType === 'usdt' && withdrawalAmountExceedsMax);
	  const setSafeWithdrawalAmount = (rawValue: string) => {
	    if (rawValue === '') { setWithdrawalAmount(''); return; }
	    const nextAmount = parseFloat(rawValue);
	    if (!Number.isFinite(nextAmount) || nextAmount < 0) { setWithdrawalAmount(''); return; }
	    if (withdrawalType === 'usdt' && nextAmount > maxWithdrawalAmount) {
	      setWithdrawalAmount(maxWithdrawalAmount > 0 ? formatFlooredUsd(maxWithdrawalAmount) : '');
	      setError(`Maximum available withdrawal amount is $${formatFlooredUsd(maxWithdrawalAmount)}.`);
	      return;
	    }
	    setError(null);
	    setWithdrawalAmount(rawValue);
	  };
	  const clientDisplayBalance = Math.max(
	    0,
	    typeof displayedBalanceUSDT === 'number' ? displayedBalanceUSDT : availableUSDT
	  );
  const sourceOfFundsRoom = Math.max(0, computedWithdrawable || 0);

  const pendingWithdrawalLock = withdrawalBlockReasonCode === 'WITHDRAWAL_ESCROW_ACTIVE';
  const setupRequiredLock = withdrawalBlockReasonCode === 'KYC_REQUIRED' || withdrawalBlockReasonCode === 'WALLET_REQUIRED';
  const withdrawalStatusLabel = !withdrawalBlocked
    ? t('withdrawal.available', 'Available')
    : pendingWithdrawalLock
      ? t('withdrawal.status.inProgress', 'Withdrawal in Progress')
      : setupRequiredLock
        ? t('withdrawal.status.setupRequired', 'Setup Required')
        : t('withdrawal.temporarilyRestricted', 'Temporarily Restricted');
  const withdrawalStatusSubtext = !withdrawalBlocked
    ? t('withdrawal.readyToWithdraw', 'Ready to withdraw')
    : pendingWithdrawalLock
      ? t('withdrawal.status.pendingWithdrawal', 'Existing withdrawal pending')
      : setupRequiredLock
        ? t('withdrawal.status.actionNeeded', 'Action needed')
        : t('withdrawal.underReview', 'Under review');
  const withdrawalAlertTitle = pendingWithdrawalLock
    ? t('withdrawal.status.inProgress', 'Withdrawal in Progress')
    : setupRequiredLock
      ? t('withdrawal.status.setupRequired', 'Setup Required')
      : t('withdrawal.temporarilyRestricted', 'Withdrawal Temporarily Restricted');

  // Load user withdrawals and separate pending verification ones
  const loadUserWithdrawals = async () => {
    try {
      const withdrawals = await withdrawalEscrowService.getUserWithdrawals(userId);
      const allWithdrawals = withdrawals || [];

      // Separate pending_verification withdrawals for the dedicated section
      const pendingVerification = allWithdrawals.filter((w: any) => w.status === 'pending_verification');
      const otherWithdrawals = allWithdrawals.filter((w: any) => w.status !== 'pending_verification');

      setPendingVerificationWithdrawals(pendingVerification);
      setUserWithdrawals(otherWithdrawals);
    } catch (error) {
      console.error('Error loading user withdrawals:', error);
    }
  };

  // Handle resending verification link for a pending withdrawal
  const handleResendVerification = async (withdrawalId: string) => {
    setResendingVerification(withdrawalId);
    try {
      // SECURITY (v2.9.12): Use session headers — userId is derived server-side from session
      const sessionHeaders = getSessionHeaders();

      const response = await fetch('/api/resend-withdrawal-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ withdrawalId })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ A new verification link has been sent to your email. Please check your inbox and click the link to verify your withdrawal.');
      } else {
        alert(`❌ Failed to resend verification: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error resending verification:', error);
      alert('❌ Failed to resend verification link. Please try again.');
    } finally {
      setResendingVerification(null);
    }
  };

  // Handle cancelling a pending verification withdrawal
  const handleCancelPendingWithdrawal = async (withdrawalId: string) => {
    if (!confirm('Are you sure you want to cancel this withdrawal? Your funds will be refunded to your balance.')) {
      return;
    }

    setCancellingPendingId(withdrawalId);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch('/api/cancel-withdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({ withdrawalId })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Withdrawal cancelled. Your funds have been refunded to your balance.');
        onWithdrawalRequest();
        loadUserWithdrawals();
      } else {
        alert(`❌ Failed to cancel: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error cancelling withdrawal:', error);
      alert('❌ Failed to cancel withdrawal. Please try again.');
    } finally {
      setCancellingPendingId(null);
    }
  };

  // Load KYC status on component mount
  useEffect(() => {
    const checkKYCStatus = async () => {
      setLoading(true);
      try {
        const validation = await validateKYCForFinancialOperations(userId);
        setKycValidation(validation);
      } catch (error) {
        console.error('Error checking KYC status:', error);
        setKycValidation({
          isValid: false,
          status: 'not_started',
          message: 'Error checking KYC status. Please try again.',
          canWithdraw: false,
          canReceiveDividends: false
        });
      } finally {
        setLoading(false);
      }
    };

    checkKYCStatus();
    loadUserWithdrawals();

    // Check if withdrawals are enabled system-wide
    const checkWithdrawalsEnabled = async () => {
      try {
        const headers = getSessionHeaders();
        const res = await fetch('/api/user/system-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({ operation: 'check_withdrawals_enabled' })
        });
        const result = await res.json();
        if (result.success) {
          setWithdrawalsEnabled(result.withdrawalsEnabled);
        }
      } catch (err) {
        console.warn('Could not check withdrawal status:', err);
      }
    };
    checkWithdrawalsEnabled();
  }, [userId]);

  // Handle withdrawal request submission
  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Check for approved withdrawals pending confirmation
    const pendingConfirmation = userWithdrawals.filter(
      w => w.status === 'approved' && !w.user_confirmed_receipt
    );

    if (pendingConfirmation.length > 0) {
      const pendingAmount = pendingConfirmation.reduce((sum: number, w: any) => sum + parseFloat(w.withdrawal_amount), 0);
      alert(
        `⚠️ You have ${pendingConfirmation.length} approved withdrawal(s) totaling $${pendingAmount.toFixed(2)} pending your confirmation.\n\n` +
        `Please scroll down to your Withdrawal History and click "Confirm Receipt" after you have received the funds in your wallet.\n\n` +
        `You cannot request a new withdrawal until you confirm receipt of your previous withdrawal(s).`
      );
      return;
    }

    if (!kycValidation?.canWithdraw) {
      setError('KYC verification required for withdrawals.');
      return;
    }

    // SECURITY: Block withdrawal for protected admin accounts
    const PROTECTED_ADMIN_IDS = [1,4,89,102,103,104,105,107,111,113,115,123];
    if (PROTECTED_ADMIN_IDS.includes(userId)) {
      setError('Withdrawal operations are not available for admin/system accounts.');
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid withdrawal amount.');
      return;
    }

    // USDT withdrawals have network fees: $10 for ERC20 (Ethereum), $2 for others
    const __networkFee = withdrawalType === 'usdt' ? (network === 'USDT-ERC20' ? 10.00 : 2.00) : 0;

    // KYC is required for all withdrawals (v2.9.74) — enforced by canWithdraw check above

    const maxAmount = withdrawalType === 'usdt' ? maxWithdrawalAmount : availableShares;

    // For USDT, check the strict submit ceiling (source-of-funds + liquid balance after fee)
    if (withdrawalType === 'usdt' && amount > maxWithdrawalAmount) {
      setError(`Maximum available withdrawal amount is $${formatFlooredUsd(maxWithdrawalAmount)}.`);
      return;
    }

    if (withdrawalType !== 'usdt' && amount > maxAmount) {
      setError(`Insufficient balance. Maximum available: ${maxAmount.toFixed(2)}`);
      return;
    }

    if (withdrawalType === 'usdt' && !walletAddress.trim()) {
      setError('Please enter your wallet address.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use escrow service for all withdrawals
      if (withdrawalType === 'usdt') {
        // USDT withdrawal with $2 network fee
        const result = await withdrawalEscrowService.submitWithdrawalRequest({
          user_id: userId,
          withdrawal_amount: amount,
          wallet_address: walletAddress,
          network: network,
          currency: 'USDT'
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to submit withdrawal request');
        }

        // Magic link verification flow - show email confirmation screen
        if (result.requiresVerification && result.withdrawalRequestId) {
          const submittedAmount = parseFloat(withdrawalAmount);
          const submittedWallet = walletAddress;
          const submittedNetwork = network;
          setWithdrawalAmount('');
          setWalletAddress('');
          onWithdrawalRequest();
          loadUserWithdrawals();
          // Show the email confirmation screen (with warning if email failed)
          setEmailConfirmationData({
            withdrawalId: result.withdrawalRequestId,
            amount: submittedAmount,
            walletAddress: submittedWallet,
            network: submittedNetwork,
            linkSent: result.linkSent !== false,
            expiresAt: result.expiresAt || null,
          });
          return;
        }
      } else {
        // Share withdrawal with proper escrow
        const result = await withdrawalEscrowService.submitShareWithdrawalRequest(userId, amount);

        if (!result.success) {
          throw new Error(result.error || 'Failed to submit share withdrawal request');
        }
      }

      setSuccess(true);
      setWithdrawalAmount('');
      setWalletAddress('');
      onWithdrawalRequest();
      loadUserWithdrawals(); // Refresh withdrawal list

      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

    } catch (error: any) {
      console.error('Error submitting withdrawal request:', error);
      setError(error.message || 'Failed to submit withdrawal request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle withdrawal confirmation
  const handleConfirmReceipt = async (withdrawalId: string) => {
    setConfirmingWithdrawal(withdrawalId);
    try {
      const result = await withdrawalEscrowService.confirmReceipt(withdrawalId, userId);

      if (result.success) {
        alert('✅ Withdrawal confirmed successfully! Thank you.');
        loadUserWithdrawals(); // Refresh the list
      } else {
        alert(`❌ Failed to confirm withdrawal: ${result.error}`);
      }
    } catch (error) {
      console.error('Error confirming withdrawal:', error);
      alert('❌ Error confirming withdrawal. Please try again.');
    } finally {
      setConfirmingWithdrawal(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-300">{t('kyc.checkingStatus', 'Checking KYC status...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6" translate="no">
      <h3 className="text-xl font-bold text-white mb-4">💸 {t('withdrawal.heading', 'Commission Withdrawal')}</h3>

      {/* KYC Status Display */}
      <div className={`rounded-lg p-4 mb-6 ${
        kycValidation?.canWithdraw
          ? 'bg-green-900/20 border border-green-500/30'
          : 'bg-yellow-900/20 border border-yellow-500/30'
      }`}>
        <div className="flex items-start space-x-3">
          <div className="text-2xl">
            {kycValidation?.canWithdraw ? '✅' : '⚠️'}
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-white mb-1">
              {kycValidation?.canWithdraw ? 'KYC Verified' : 'KYC Required'}
            </h4>
            <p className={`text-sm ${
              kycValidation?.canWithdraw ? 'text-green-300' : 'text-yellow-300'
            }`}>
              {kycValidation?.message}
            </p>



            {!kycValidation?.canWithdraw && (
              <button
                onClick={() => window.location.href = '/kyc-certification'}
                className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm px-3 py-1 rounded transition-colors"
              >
                {t('kyc.completeNow', 'Complete KYC Now')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Withdrawal Availability */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-1">{t('withdrawal.availableToWithdrawNow', 'Available to withdraw now')}</h4>
          <p className="text-2xl font-bold text-blue-400">${formatFlooredUsd(maxWithdrawalAmount)}</p>
          <p className="text-sm text-gray-300 mt-1">Uses live backend withdrawal max</p>
          <div className="mt-2 space-y-1 text-xs text-gray-300">
            <div>Raw USDT commission balance: ${clientDisplayBalance.toFixed(2)}</div>
            <div>Verified source-of-funds room: ${sourceOfFundsRoom.toFixed(2)}</div>
            <div>Network fee: ${formatFlooredUsd(withdrawalNetworkFee)}</div>
          </div>
        </div>
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-1">{t('withdrawal.status', 'Withdrawal Status')}</h4>
          <p className={`text-2xl font-bold ${withdrawalBlocked ? 'text-amber-400' : 'text-green-400'}`}>
            {withdrawalStatusLabel}
          </p>
          <p className="text-sm text-gray-300 mt-1">{withdrawalStatusSubtext}</p>
        </div>
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-1">{t('withdrawal.availableShares', 'Available Shares')}</h4>
          <p className="text-2xl font-bold text-yellow-400">{Math.floor(availableShares).toLocaleString()}</p>
          <p className="text-sm text-white">{currentSharePrice > 0 ? `≈ $${(Math.floor(availableShares) * currentSharePrice).toFixed(2)}` : '...'}</p>
          {DIVIDENDS_USER_VISIBLE && availableShares - Math.floor(availableShares) > 0 && (
            <p className="text-sm text-blue-300 mt-1">
              ℹ️ {t('withdrawal.dividendNote', 'Only whole shares qualify for dividends')}
            </p>
          )}
        </div>
      </div>

      {withdrawalBlocked && (
        <div className="rounded-lg p-4 mb-6 bg-amber-900/20 border border-amber-500/30">
          <h4 className="text-sm font-semibold text-amber-300 mb-1">{withdrawalAlertTitle}</h4>
          <p className="text-sm text-amber-100">
            {withdrawalBlockReasonMessage || t('withdrawal.restrictedMessage', 'Your withdrawal is temporarily restricted. Please contact support if you need assistance.')}
          </p>
        </div>
      )}

      {/* Pending Verification Withdrawals Section */}
      {pendingVerificationWithdrawals.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="text-2xl">⏳</div>
            <div>
              <h4 className="text-lg font-bold text-yellow-400">Pending Verification</h4>
              <p className="text-yellow-300 text-sm">
                The following withdrawal(s) are awaiting your email verification.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {pendingVerificationWithdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="bg-gray-800 rounded-lg p-4 border border-yellow-500/30">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-yellow-400 font-semibold text-lg">
                      ${withdrawal.withdrawal_amount?.toFixed(2)} {withdrawal.currency || 'USDT'}
                    </div>
                    <div className="text-white text-sm">
                      Requested: {new Date(withdrawal.created_at).toLocaleDateString()} at {new Date(withdrawal.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-900/50 text-yellow-400 border border-yellow-500/50">
                    AWAITING VERIFICATION
                  </span>
                </div>

                {withdrawal.wallet_address && (
                  <div className="text-white text-sm mb-2">
                    <span className="font-medium">Wallet:</span>{' '}
                    <span className="font-mono text-gray-300">
                      {withdrawal.wallet_address.substring(0, 12)}...{withdrawal.wallet_address.slice(-6)}
                    </span>
                  </div>
                )}

                {withdrawal.network && (
                  <div className="text-white text-sm mb-3">
                    <span className="font-medium">Network:</span> {withdrawal.network}
                  </div>
                )}

                <div className="bg-yellow-900/30 border border-yellow-500/30 rounded p-3 mb-4">
                  <p className="text-yellow-300 text-sm">
                    📧 A verification link was sent to your email. Click the link to verify your withdrawal.
                    If you didn't receive it, click "Resend Link".
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={() => handleResendVerification(withdrawal.id)}
                    disabled={resendingVerification === withdrawal.id}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors text-sm ${
                      resendingVerification === withdrawal.id
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {resendingVerification === withdrawal.id ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending...
                      </span>
                    ) : (
                      '📧 Resend Link'
                    )}
                  </button>
                  <button
                    onClick={() => handleCancelPendingWithdrawal(withdrawal.id)}
                    disabled={cancellingPendingId === withdrawal.id}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors text-sm ${
                      cancellingPendingId === withdrawal.id
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {cancellingPendingId === withdrawal.id ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Cancelling...
                      </span>
                    ) : (
                      '❌ Cancel'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Non-dismissible Email Verification Modal — shared component */}
      {emailConfirmationData && (
        <WithdrawalEmailVerificationModal
          confirmationData={emailConfirmationData}
          userEmail={userEmail || ''}
          onClose={() => {
            setEmailConfirmationData(null);
            onWithdrawalRequest();
            loadUserWithdrawals();
          }}
          onDataRefresh={() => {
            onWithdrawalRequest();
            loadUserWithdrawals();
          }}
        />
      )}

      {/* Withdrawal Form */}
      {emailConfirmationData ? null : isMasterAdminViewSession() ? (
        <div className="bg-gray-700 border border-yellow-500 rounded-lg p-6 text-center">
          <div className="text-yellow-500 text-3xl mb-3">🔒</div>
          <h4 className="text-lg font-bold text-white mb-2">
            Withdrawal Disabled - Read-Only Mode
          </h4>
          <p className="text-white text-sm">
            Financial operations cannot be performed while in admin view mode.
          </p>
        </div>
      ) : !withdrawalsEnabled ? (
        <div className="bg-yellow-900/30 border border-yellow-500/40 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <h4 className="font-semibold text-yellow-300">Withdrawals Temporarily Disabled</h4>
              <p className="text-yellow-200 text-sm mt-1">
                Withdrawals are temporarily disabled by the administrator. Please try again later.
              </p>
            </div>
          </div>
        </div>
      ) : kycValidation?.canWithdraw ? (
        <>
        {/* Locked Wallet Manager */}
        <LockedWalletManager userId={userId} onWalletsLoaded={handleLockedWalletsLoaded} />

        {/* Block withdrawal form if user has no BSC wallet registered */}
        {lockedWallets.length > 0 && withdrawalType === 'usdt' && !walletLocked && (
          <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-4 mb-4">
            <p className="text-red-300 text-sm">⚠️ You do not have a registered BSC wallet. Please register a BSC wallet above before you can withdraw.</p>
          </div>
        )}

        <form onSubmit={handleWithdrawalSubmit} className="space-y-4">
          {/* Withdrawal Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Withdrawal Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="usdt"
                  checked={withdrawalType === 'usdt'}
                  onChange={(e) => setWithdrawalType(e.target.value as 'usdt')}
                  className="mr-2"
                />
                <span className="text-white">USDT</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="shares"
                  checked={withdrawalType === 'shares'}
                  onChange={(e) => setWithdrawalType(e.target.value as 'shares')}
                  className="mr-2"
                />
                <span className="text-white">Convert to Shares</span>
              </label>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Amount {withdrawalType === 'usdt' ? '(USDT)' : '(Shares)'}
            </label>
            <div className="space-y-2">
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setSafeWithdrawalAmount(e.target.value)}
                placeholder={`Min $5.00`}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                step="0.01"
                min="5"
                max={withdrawalType === 'usdt' ? maxWithdrawalAmount : availableShares}
              />
              {withdrawalType === 'usdt' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      // Recommended: withdraw 80% of max available (leaving some buffer)
                      const maxWithdrawable = maxWithdrawalAmount;
                      const recommended = floorToCents(Math.min(maxWithdrawable, maxWithdrawable * 0.8));
                      if (recommended >= 5) {
                        setWithdrawalAmount(formatFlooredUsd(recommended));
                      } else if (maxWithdrawable >= 5) {
                        setWithdrawalAmount('5');
                      }
                    }}
                    disabled={maxWithdrawalAmount < 5}
                    className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors whitespace-nowrap"
                    title="Set recommended amount (80% of max)"
                  >
                    Recommended
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const maxWithdrawable = maxWithdrawalAmount;
                      if (maxWithdrawable >= 5) {
                        setWithdrawalAmount(formatFlooredUsd(maxWithdrawable));
                      }
                    }}
                    disabled={maxWithdrawalAmount < 5}
                    className="flex-1 px-3 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold text-sm rounded-lg transition-colors"
                    title="Set maximum amount"
                  >
                    Max
                  </button>
                </div>
              )}
            </div>
            {/* USDT Fee Notice */}
            {withdrawalType === 'usdt' && (
              <div className="mt-2 bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-blue-300 text-sm">
                  <span>ℹ️</span>
                  <span>A <strong>${formatFlooredUsd(withdrawalNetworkFee)} network fee</strong> applies to all withdrawals</span>
                </div>
                {withdrawalAmount && parseFloat(withdrawalAmount) > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-500/30 text-sm" translate="no">
                    <div className="flex justify-between text-gray-300">
                      <span>You Will Receive:</span>
                      <span translate="no">${parseFloat(withdrawalAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Network Fee:</span>
                      <span translate="no">+${formatFlooredUsd(withdrawalNetworkFee)}</span>
                    </div>
                    <div className="flex justify-between text-yellow-400 font-semibold mt-1 pt-1 border-t border-blue-500/20">
                      <span>Total Deducted from Balance:</span>
                      <span translate="no">${(parseFloat(withdrawalAmount) + withdrawalNetworkFee).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                {withdrawalType === 'usdt' && (
                  <p className="text-xs text-amber-300">
                    Maximum available withdrawal amount is ${formatFlooredUsd(maxWithdrawalAmount)}.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Network & Wallet Address (only for USDT) — BSC ONLY (v4.0.53) */}
          {withdrawalType === 'usdt' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Network
                </label>
                <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white">
                  BSC (BNB Smart Chain) — USDT BEP-20
                </div>
                <p className="text-blue-300 text-xs mt-1">ℹ️ BSC is the only supported withdrawal network. Other networks have been removed due to high transaction fees and because the Aureus Smart Contract only supports BSC withdrawals.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Wallet Address {walletLocked && <span className="text-green-400 text-xs ml-1">🔒 Locked</span>}
                </label>
                {walletLocked ? (
                  <div className="w-full bg-gray-600 border border-green-600/50 rounded-lg px-3 py-2 text-white font-mono text-sm select-none">
                    {walletAddress}
                  </div>
                ) : (
                  <div className="w-full bg-red-900/30 border border-red-500/40 rounded-lg px-3 py-2 text-red-300 text-sm">
                    🔒 You must register a BSC withdrawal wallet before you can withdraw. Use the "Register BSC Wallet" section above.
                  </div>
                )}
                {walletLocked && (
                  <div className="mt-2">
                    <p className="text-green-400 text-xs">🔒 This address is locked to your account and cannot be changed.</p>
                    {onNavigateToSupport && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={onNavigateToSupport}
                          className="text-xs bg-gray-600 hover:bg-gray-500 text-yellow-300 px-3 py-1.5 rounded transition-colors"
                        >
                          📩 Request Wallet Address Change
                        </button>
                        <p className="text-gray-500 text-xs mt-1">To change your locked wallet address, submit a support ticket. An administrator will verify your identity before updating your wallet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
              <p className="text-green-400 text-sm">
                ✅ Withdrawal request submitted successfully! It will be reviewed by our team.
              </p>
            </div>
          )}

          {/* Submit Button — SECURITY (v4.0.52): Disabled unless wallet is locked for USDT withdrawals */}
          <button
            type="submit"
            disabled={withdrawalSubmitDisabled}
            className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {isSubmitting ? t('common.submitting', 'Submitting...') : (withdrawalType === 'usdt' && !walletLocked) ? `🔒 ${t('withdrawal.submit.registerWalletFirst', 'Register a Wallet First')}` : (withdrawalType === 'usdt' ? t('withdrawal.submit.usdt', 'Request USDT Withdrawal') : t('withdrawal.submit.shares', 'Request Share Withdrawal'))}
          </button>
        </form>
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-white mb-4">{t('withdrawal.kycRequired.body', 'Complete KYC verification to withdraw your commissions.')}</p>
          <button
            onClick={() => window.location.href = '/kyc-certification'}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            Start KYC Verification
          </button>
        </div>
      )}

      {/* Withdrawal History & Confirmations */}
      {userWithdrawals.length > 0 && (
        <div className="mt-8 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">📋 Your Withdrawal History</h3>

          <div className="space-y-4">
            {userWithdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-yellow-400 font-semibold text-lg">
                      ${withdrawal.withdrawal_amount?.toFixed(2)} {withdrawal.currency}
                    </div>
                    <div className="text-white text-sm">
                      {new Date(withdrawal.created_at).toLocaleDateString()} at {new Date(withdrawal.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      withdrawal.status === 'pending' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' :
                      withdrawal.status === 'processing' ? 'bg-blue-900/30 text-blue-400 border border-blue-500/30' :
                      withdrawal.status === 'completed' ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                      withdrawal.status === 'rejected' ? 'bg-red-900/30 text-red-400 border border-red-500/30' :
                      'bg-gray-900/30 text-white border border-gray-500/30'
                    }`}>
                      {withdrawal.status?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {withdrawal.wallet_address && (
                  <div className="text-white text-sm mb-2">
                    <span className="font-medium">Wallet:</span> {withdrawal.wallet_address.substring(0, 10)}...{withdrawal.wallet_address.substring(-6)}
                  </div>
                )}

                {withdrawal.network && (
                  <div className="text-white text-sm mb-2">
                    <span className="font-medium">Network:</span> {withdrawal.network}
                  </div>
                )}

                {/* Transaction Hash for Approved/Completed Withdrawals */}
                {(withdrawal.status === 'approved' || withdrawal.status === 'completed') && withdrawal.transaction_hash && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-3 mt-3">
                    <div className="text-green-400 text-sm mb-2">
                      <span className="font-medium">✅ Transaction Hash:</span>
                    </div>
                    <div className="bg-gray-800 p-2 rounded text-sm font-mono text-gray-300 break-all">
                      {withdrawal.transaction_hash}
                    </div>
                    {withdrawal.proof_of_payment_url && (
                      <div className="mt-2">
                        <button
                          onClick={() => {
                            setSelectedProof(withdrawal);
                            setProofViewerOpen(true);
                          }}
                          className="inline-flex items-center text-green-400 hover:text-green-300 text-sm transition-colors"
                        >
                          📄 View Proof of Payment →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {withdrawal.rejection_reason && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded p-3 mt-3">
                    <div className="text-red-400 text-sm">
                      <span className="font-medium">❌ Rejection Reason:</span> {withdrawal.rejection_reason}
                    </div>
                  </div>
                )}

                {/* Processing Status - Awaiting User Confirmation */}
                {withdrawal.status === 'processing' && withdrawal.proof_of_payment_url && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mt-3">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-blue-400 font-medium">✅ Payment Sent - Please Confirm Receipt</h4>
                        <p className="text-white text-sm mt-1">
                          Your withdrawal has been processed. Please confirm that you received the funds.
                        </p>
                      </div>
                    </div>

                    {withdrawal.transaction_hash && (
                      <div className="mb-3">
                        <div className="text-white text-sm mb-1">
                          <span className="font-medium">Transaction Hash:</span>
                        </div>
                        <div className="bg-gray-800 p-2 rounded text-sm font-mono text-gray-300 break-all">
                          {withdrawal.transaction_hash}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSelectedProof(withdrawal);
                          setProofViewerOpen(true);
                        }}
                        className="flex-1 bg-gray-600 hover:bg-gray-500 text-white text-center py-2 px-4 rounded-lg transition-colors text-sm"
                      >
                        📄 View Proof of Payment
                      </button>
                      <button
                        onClick={() => handleConfirmReceipt(withdrawal.id)}
                        disabled={confirmingWithdrawal === withdrawal.id}
                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors text-sm"
                      >
                        {confirmingWithdrawal === withdrawal.id ? 'Confirming...' : '✅ Confirm Receipt'}
                      </button>
                    </div>
                  </div>
                )}

                {withdrawal.status === 'completed' && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded p-3 mt-3">
                    <div className="text-green-400 text-sm">
                      ✅ <span className="font-medium">Completed:</span> Withdrawal confirmed and processed successfully.
                    </div>
                    {withdrawal.transaction_hash && (
                      <div className="text-white text-sm mt-1 font-mono">
                        TX: {withdrawal.transaction_hash}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proof of Payment Viewer Modal */}
      {selectedProof && (
        <ProofOfPaymentViewer
          isOpen={proofViewerOpen}
          onClose={() => {
            setProofViewerOpen(false);
            setSelectedProof(null);
          }}
          proofUrl={selectedProof.proof_of_payment_url}
          transactionHash={selectedProof.transaction_hash}
          amount={selectedProof.withdrawal_amount}
          currency={selectedProof.currency || 'USDT'}
          withdrawalDate={selectedProof.created_at}
        />
      )}
    </div>
  );
};
